import type { Account, UserWRole } from "@repo/types";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { Button } from "@repo/ui/components/Base/Button";
import { MoreHorizontal } from "lucide-react";
import { DataTableColumnHeader } from "@repo/ui/components/Base/DataTableColumnHeader";
import {
  CacheMap,
  createAddressColumn,
  createCreatedAtColumn,
  createIdColumn,
  createPhoneColumn,
  createRoleColumn,
  createUpdatedAtColumn,
} from "../dataTable/columns";
import type { TableLayoutState } from "@repo/ui/components/Base/DataTable";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useMemo } from "react";
import { arrayify } from "@repo/shared/utils";
import { UserSignedUp } from "./UserSignedUp";
import { accountRepo } from "@/repositories/accountRepo";

export const createFirstNameColumn = (): ColumnDef<UserWRole> => ({
  accessorKey: "firstName",
  meta: {
    label: "First Name",
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="First Name" />
  ),
});

export const createLastNameColumn = (): ColumnDef<UserWRole> => ({
  accessorKey: "lastName",
  meta: {
    label: "Last Name",
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Last Name" />
  ),
});

export const createEmailColumn = (): ColumnDef<UserWRole> => ({
  accessorKey: "email",
  meta: {
    label: "Email",
  },
  sortingFn: "text",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Email" />
  ),
});

class AccountByEmailMap extends CacheMap<Account> {
  constructor() {
    super((_tenantId: string, email: string) => accountRepo.getByEmail(email));
  }
}

export const createSignedUpColumn = (): ColumnDef<UserWRole> => ({
  id: "signedUp",
  accessorKey: "email",
  filterFn: "async",
  meta: {
    label: "Signed Up",
    filterVariant: "customBoolean",
    customBooleanOptions: {
      trueLabel: "Signed Up",
      falseLabel: "Not Signed Up",
    },
    async asyncFilterFn(row, _columnId, rawFilterValue, _table, rawState) {
      const state = rawState as { accountMap?: AccountByEmailMap };
      state.accountMap ??= new AccountByEmailMap();

      const filterValue = rawFilterValue as boolean;
      const account = await state.accountMap.get("", row.original.email);

      return filterValue === !!account;
    },
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Signed Up" noSort />
  ),
  cell: ({ row }) => {
    const user = row.original;
    return <UserSignedUp user={user} />;
  },
});

export const useColumns = ({
  handleEdit,
  handleDelete,
}: {
  handleEdit: (id: string) => void;
  handleDelete: (id: string) => void;
}) => {
  handleEdit = useFutureCallback(handleEdit);
  handleDelete = useFutureCallback(handleDelete);

  const col: ColumnDef<UserWRole>[] = useMemo(
    () => [
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const user = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleEdit(user.id)}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDelete(user.id)}>
                  Remove
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigator.clipboard.writeText(user.id)}
                >
                  Copy User ID
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableHiding: false,
      },
      createIdColumn(),
      createFirstNameColumn(),
      createLastNameColumn(),
      createEmailColumn(),
      createRoleColumn<UserWRole>(),
      createSignedUpColumn(),
      createAddressColumn((row) => row.primaryAddress),
      createPhoneColumn((row) =>
        row.primaryPhone ? arrayify(row.primaryPhone) : []
      ),
      createCreatedAtColumn(),
      createUpdatedAtColumn(),
    ],
    [handleEdit, handleDelete]
  );
  return col;
};

export const layoutDefaults: TableLayoutState = {
  columnVisibility: {
    id: false,
    updatedAt: false,
  },
};
